/** Domain cards: categories, partners, services, products, jobs, vehicles. */
import { useState } from 'react';
import { colors } from '../theme';
import { Badge, Rating } from './Ui';
import { fileUrl } from '../api/client';
import {
  currency, discountPercent, salaryText, titleCase, dayMonth,
} from '../utils/format';

/** Square icon tile used in the category grids. */
export const CategoryTile = ({ category, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="press"
    style={{
      background: 'none', border: 0, padding: 0,
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
    }}
  >
    <span
      style={{
        width: 56, height: 56, borderRadius: 16,
        background: `${category.color || colors.primary}18`,
        display: 'grid', placeItems: 'center', fontSize: 26,
      }}
    >
      {category.icon || '📁'}
    </span>
    <span className="t-tiny clamp-2" style={{ color: colors.ink2, textAlign: 'center', lineHeight: 1.2 }}>
      {category.name}
    </span>
  </button>
);

/**
 * Card image with a graceful fallback.
 *
 * A listing whose photo has been deleted from storage would otherwise render
 * the browser's broken-image glyph, which looks like the page is broken rather
 * than the picture - so a failed load falls back to the category emoji.
 */
const Cover = ({ src, fallback, height = 110, radius = 0 }) => {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <div
        style={{
          width: '100%', height, borderRadius: radius,
          background: colors.primaryLight, display: 'grid', placeItems: 'center', fontSize: 30,
        }}
      >
        {fallback}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
      style={{ width: '100%', height, objectFit: 'cover', borderRadius: radius, background: colors.bg }}
    />
  );
};

/** Wide card for a business / service provider. */
export const PartnerCard = ({ partner, onClick, style, onCall, onDirections, onTour, onProducts }) => (
  <div
    className="card sh-sm press hoverable"
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
    style={{ padding: 0, overflow: 'hidden', ...style }}
  >
    <Cover
      src={fileUrl(partner.coverImage || partner.logo)}
      fallback={(partner.primaryCategory && partner.primaryCategory.icon) || '🏪'}
      height={126}
    />
    <div style={{ padding: 12 }}>
      <div className="row-between">
        <div className="t-title clamp-1 grow">{partner.businessName}</div>
        <Rating value={partner.rating} count={partner.ratingCount} />
      </div>
      <div className="t-small clamp-1" style={{ marginTop: 3 }}>
        {(partner.primaryCategory && partner.primaryCategory.name) || titleCase(partner.partnerType)}
        {partner.address && partner.address.city ? ` · ${partner.address.city}` : ''}
      </div>
      <div className="row-between" style={{ marginTop: 8 }}>
        <div className="row" style={{ gap: 6 }}>
          {partner.distanceKm !== undefined ? (
            <span className="pill">📍 {partner.distanceKm} km</span>
          ) : null}
          {partner.deliveryAvailable ? <span className="pill">🛵 Delivery</span> : null}
        </div>
        <Badge
          value={partner.isOpen ? 'Open' : 'Closed'}
          color={partner.isOpen ? colors.success : colors.danger}
        />
      </div>

      {onCall || onDirections || onTour || onProducts ? (
        <div className="row wrap card-actions">
          {onProducts ? (
            <button type="button" className="mini-btn" onClick={(e) => { e.stopPropagation(); onProducts(); }}>📦 Products</button>
          ) : null}
          {onTour && (partner.tour || []).length ? (
            <button type="button" className="mini-btn" onClick={(e) => { e.stopPropagation(); onTour(); }}>🎥 360°</button>
          ) : null}
          {onCall ? (
            <button type="button" className="mini-btn" onClick={(e) => { e.stopPropagation(); onCall(); }}>📞 Call</button>
          ) : null}
          {onDirections ? (
            <button type="button" className="mini-btn" onClick={(e) => { e.stopPropagation(); onDirections(); }}>🧭 Directions</button>
          ) : null}
        </div>
      ) : null}
    </div>
  </div>
);

/** Horizontal service row used in category listings. */
/** "Department: Dental · Appointment: Yes · Warranty: 30 days" - at a glance. */
export const detailLine = (item, max = 3) => [
  ...((item.attributes || []).map((a) => `${a.label}: ${a.values.map((v) => (v === 'yes' ? 'Yes' : v === 'no' ? 'No' : v)).join(', ')}`)),
  ...((item.specs || []).map((s) => `${s.key}: ${s.value}`)),
].filter(Boolean).slice(0, max).join(' · ');

export const ServiceCard = ({ service, onClick, style }) => {
  const off = discountPercent(service.mrp, service.price);
  return (
    <div
      className="card sh-sm press hoverable"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
      style={{ display: 'flex', gap: 12, padding: 12, ...style }}
    >
      <div style={{ width: 82, flex: '0 0 82px' }}>
        <Cover
          src={service.images && service.images[0] ? fileUrl(service.images[0]) : null}
          fallback={(service.category && service.category.icon) || '🛠️'}
          height={82}
          radius={12}
        />
      </div>
      <div className="grow">
        <div className="t-title clamp-2">{service.title}</div>
        <div className="t-small clamp-1" style={{ marginTop: 2 }}>
          {(service.partner && service.partner.businessName) || ''}
        </div>
        {/* What the category asks about, then the provider's own details. */}
        {detailLine(service) ? <div className="t-tiny clamp-2">{detailLine(service)}</div> : null}
        <div className="row-between" style={{ marginTop: 7 }}>
          <div className="row" style={{ gap: 6, alignItems: 'baseline' }}>
            <span style={{ fontSize: 15, fontWeight: 800 }}>{currency(service.price)}</span>
            {off > 0 ? (
              <span style={{ fontSize: 12, color: colors.faint, textDecoration: 'line-through' }}>
                {currency(service.mrp)}
              </span>
            ) : null}
            {off > 0 ? (
              <span style={{ fontSize: 11, color: colors.success, fontWeight: 700 }}>{off}% off</span>
            ) : null}
          </div>
          <Rating value={service.rating} />
        </div>
        {service.durationMinutes ? (
          <div className="t-tiny" style={{ marginTop: 4 }}>
            ⏱ {service.durationMinutes} mins · {service.priceUnit}
          </div>
        ) : null}
      </div>
    </div>
  );
};

/** Compact product tile for shop catalogues and rails. */
export const ProductCard = ({ product, onClick, onAdd, inCart = 0, width, style }) => {
  const off = discountPercent(product.mrp, product.price);
  return (
    <div
      className="card sh-sm hoverable"
      style={{ padding: 12, width, display: 'flex', flexDirection: 'column', ...style }}
    >
      <div
        style={{ position: 'relative', cursor: 'pointer' }}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
      >
        <Cover
          src={product.images && product.images[0] ? fileUrl(product.images[0]) : null}
          fallback={(product.category && product.category.icon) || '📦'}
          height={104}
          radius={12}
        />
        {off > 0 ? (
          <span
            style={{
              position: 'absolute', top: 6, left: 6, background: colors.accent, color: '#fff',
              padding: '2px 6px', borderRadius: 5, fontSize: 9.5, fontWeight: 800,
            }}
          >
            {off}% OFF
          </span>
        ) : null}
      </div>
      <div
        className="t-body clamp-2"
        style={{ fontWeight: 700, marginTop: 8, minHeight: 36, cursor: 'pointer' }}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
      >
        {product.title}
      </div>
      <div className="t-tiny">{product.unit}</div>
      {/* Size, colour, who it is for - whatever this category asks about. */}
      {detailLine(product, 2) ? <div className="t-tiny clamp-1">{detailLine(product, 2)}</div> : null}
      <div className="row-between" style={{ marginTop: 6 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 800 }}>{currency(product.price)}</div>
          {off > 0 ? (
            <div style={{ fontSize: 12, color: colors.faint, textDecoration: 'line-through' }}>
              {currency(product.mrp)}
            </div>
          ) : null}
        </div>
        {onAdd ? (
          <button
            type="button"
            onClick={onAdd}
            disabled={!product.inStock}
            className="press"
            style={{
              background: colors.primary, color: '#fff', border: 0,
              padding: '6px 12px', borderRadius: 8, fontWeight: 800, fontSize: 12,
              opacity: product.inStock ? 1 : 0.5,
            }}
          >
            {inCart > 0 ? `${inCart} ✓` : 'ADD'}
          </button>
        ) : null}
      </div>
      {!product.inStock ? (
        <div className="t-tiny" style={{ color: colors.danger, marginTop: 4 }}>Out of stock</div>
      ) : null}
    </div>
  );
};

export const JobCard = ({ job, onClick, style, saved, onToggleSave }) => (
  <div
    className="card sh-sm press hoverable"
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
    style={style}
  >
    <div className="row-between" style={{ alignItems: 'flex-start' }}>
      <div className="grow">
        <div className="t-title clamp-2">{job.title}</div>
        <div className="t-small clamp-1" style={{ marginTop: 2 }}>
          {job.companyName || 'KAAMWALA partner'}
        </div>
      </div>
      {onToggleSave ? (
        <button
          type="button"
          className="icon-btn"
          title={saved ? 'Remove from saved jobs' : 'Save this job'}
          onClick={(e) => { e.stopPropagation(); onToggleSave(); }}
        >
          {saved ? '🔖' : '📑'}
        </button>
      ) : null}
      <span
        style={{
          width: 44, height: 44, borderRadius: 12, background: colors.primaryLight,
          display: 'grid', placeItems: 'center', fontSize: 20, flex: '0 0 auto',
        }}
      >
        {(job.category && job.category.icon) || '💼'}
      </span>
    </div>
    <div className="row wrap" style={{ gap: 6, marginTop: 12 }}>
      <span className="pill">{titleCase(job.jobType)}</span>
      {job.city ? <span className="pill">📍 {job.city}</span> : null}
      {job.openings > 1 ? <span className="pill">{job.openings} openings</span> : null}
    </div>
    <div className="row-between" style={{ marginTop: 12 }}>
      <span className="t-body" style={{ color: colors.success, fontWeight: 700 }}>{salaryText(job)}</span>
      <span className="t-tiny">{job.applicationCount || 0} applied</span>
    </div>
  </div>
);

export const VehicleCard = ({ vehicle, onClick, width, style }) => (
  <div
    className="card sh-sm press hoverable"
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
    style={{ padding: 0, overflow: 'hidden', width, ...style }}
  >
    <Cover
      src={vehicle.images && vehicle.images[0] ? fileUrl(vehicle.images[0]) : null}
      fallback="🚗"
      height={130}
    />
    <div style={{ padding: 12 }}>
      <div className="t-title clamp-1">{vehicle.title}</div>
      <div className="t-tiny clamp-1" style={{ marginTop: 2 }}>
        {[
          vehicle.year,
          vehicle.kmDriven ? `${vehicle.kmDriven.toLocaleString('en-IN')} km` : null,
          titleCase(vehicle.fuelType),
        ].filter(Boolean).join(' · ')}
      </div>
      <div className="row-between" style={{ marginTop: 7 }}>
        <span style={{ fontSize: 15, fontWeight: 800 }}>{currency(vehicle.price)}</span>
        {vehicle.city ? <span className="t-tiny">📍 {vehicle.city}</span> : null}
      </div>
    </div>
  </div>
);

/** Order / booking summary row used in the activity tabs. */
export const ActivityCard = ({
  title, subtitle, amount, status, date, icon, onClick, style,
}) => (
  <div
    className="card sh-sm press hoverable"
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => { if (e.key === 'Enter' && onClick) onClick(); }}
    style={{ display: 'flex', gap: 12, alignItems: 'center', padding: 12, ...style }}
  >
    <span
      style={{
        width: 46, height: 46, borderRadius: 12, background: colors.primaryLight,
        display: 'grid', placeItems: 'center', fontSize: 20, flex: '0 0 auto',
      }}
    >
      {icon}
    </span>
    <div className="grow">
      <div className="t-title clamp-1">{title}</div>
      <div className="t-small clamp-1" style={{ marginTop: 2 }}>{subtitle}</div>
      <div className="row" style={{ gap: 8, marginTop: 7 }}>
        <Badge value={status} />
        {date ? <span className="t-tiny">{dayMonth(date)}</span> : null}
      </div>
    </div>
    <div className="t-title">{currency(amount)}</div>
  </div>
);

const Cards = {
  CategoryTile, PartnerCard, ServiceCard, ProductCard, JobCard, VehicleCard, ActivityCard,
};
export default Cards;
